import {getColorsByMode, chartSeriesColors} from '_themes/tokens/colors';
import {fontFamily} from '_themes/tokens/typography';
import * as echarts from 'echarts/core';

/**
 * 共通 Echartsスタイル
 * @param prm
 * @returns
 */
const getEchartsThemeBase = (prm: {
  bgColor: string;
  fontColor: string;
  fontSubColor: string;
  fontLabelColor: string;
  iconColor: string;
  lineColor: string;
  splitColor: string;
  primaryColor: string;
  secondaryColor: string;
}): any => {
  const AXIS = {
    nameTextStyle: {
      // 軸名
      color: prm.fontSubColor,
      fontSize: 14
    },
    axisLabel: {
      // 軸目盛り文言
      show: true,
      color: prm.fontSubColor,
      fontSize: 14
    },
    axisLine: {
      // 軸線
      show: true,
      lineStyle: {
        color: prm.lineColor
      }
    },
    axisTick: {
      // 軸目盛り
      show: true,
      lineStyle: {
        color: prm.lineColor
      }
    },
    splitLine: {
      // 分割線
      show: true,
      lineStyle: {
        color: [prm.splitColor]
      }
    },
    splitArea: {
      // 交互色
      show: false
    },
    boundaryGap: [0, 0.1] // 軸値の下限上限範囲
  };

  const EmphasisAndSelect = {
    emphasis: {
      itemStyle: {
        borderColor: prm.lineColor,
        borderWidth: 8
      }
    },
    select: {
      itemStyle: {
        borderColor: prm.lineColor,
        borderWidth: 5
      }
    }
  };

  return {
    color: [...chartSeriesColors],
    backgroundColor: 'transparent',
    textStyle: {
      fontFamily
    },
    title: {
      textStyle: {
        fontSize: 16,
        fontWeight: 'normal',
        color: prm.fontColor
      },
      subtextStyle: {
        color: prm.fontSubColor
      }
    },
    line: {
      ...EmphasisAndSelect,
      label: {
        color: prm.fontSubColor
      },
      markLine: {
        label: {
          color: prm.fontSubColor,
          textBorderWidth: 0
        }
      }
    },
    bar: {
      ...EmphasisAndSelect,
      label: {
        color: prm.fontLabelColor
      }
    },
    pie: {
      ...EmphasisAndSelect,
      label: {
        color: prm.fontColor,
        textBorderWidth: 5,
        textBorderColor: prm.bgColor
      }
    },
    scatter: {
      symbolSize: 10,
      label: {
        color: prm.fontColor,
        fontWeight: 'bold'
      },
      itemStyle: {
        color: prm.fontColor
      }
    },
    scatter3D: {
      symbolSize: 10,
      label: {
        color: prm.fontColor,
        fontWeight: 'bold'
      },
      itemStyle: {
        color: prm.fontColor
      }
    },
    boxplot: {
      ...EmphasisAndSelect,
      itemStyle: {
        color: 'transparent'
      }
    },
    heatmap: {
      ...EmphasisAndSelect,
      itemStyle: {
        borderColor: prm.lineColor
      },
      markLine: {
        lineStyle: {
          color: prm.primaryColor,
          type: 'dashed',
          width: 7
        },
        symbol: 'none',
        silent: true,
        label: {
          color: prm.primaryColor,
          position: 'middle',
          fontSize: '1.2em',
          fontWeight: 'bold',
          textBorderWidth: 3,
          textBorderColor: prm.bgColor
        }
      }
    },
    // 軸
    categoryAxis: AXIS,
    valueAxis: AXIS,
    timeAxis: AXIS,
    logAxis: AXIS,
    categoryAxis3D: AXIS,
    valueAxis3D: AXIS,
    timeAxis3D: AXIS,
    logAxis3D: AXIS,
    toolbox: {
      iconStyle: {
        borderColor: prm.iconColor
      },
      emphasis: {
        iconStyle: {
          borderColor: prm.fontSubColor
        }
      }
    },
    legend: {
      textStyle: {
        fontSize: 14,
        color: prm.fontSubColor
      },
      pageIconColor: prm.iconColor,
      pageIconInactiveColor: prm.splitColor,
      pageTextStyle: {
        color: prm.fontSubColor
      },
      icon: 'circle'
    },
    tooltip: {
      axisPointer: {
        type: 'cross'
      }
    },
    visualMap: {
      textStyle: {color: prm.fontSubColor}
    },
    dataZoom: {
      backgroundColor: 'transparent',
      dataBackgroundColor: prm.lineColor,
      fillerColor: 'rgba(158, 158, 158,0.2)',
      handleColor: prm.fontColor,
      handleSize: '100%',
      textStyle: {
        color: prm.fontSubColor
      }
    },
    markPoint: {
      label: {
        show: true,
        color: prm.bgColor
      },
      itemStyle: {
        color: prm.secondaryColor
      }
    },
    markArea: {
      silent: true,
      label: {
        color: prm.fontSubColor
      },
      itemStyle: {
        color: prm.fontSubColor,
        opacity: 0.25
      }
    }
  };
};

/**
 * 定義登録
 */
(() => {
  (['light', 'dark'] as const).forEach(t => {
    const colors = getColorsByMode(t);
    echarts.registerTheme(
      t,
      getEchartsThemeBase({
        bgColor: colors.background.default,
        fontColor: colors.text.primary,
        fontSubColor: colors.text.secondary,
        fontLabelColor: colors.charts.labelColor,
        iconColor: colors.text.hint,
        lineColor: colors.charts.lineColor,
        splitColor: colors.charts.splitColor,
        primaryColor: colors.primary,
        secondaryColor: colors.secondary
      })
    );
  });
})();
